import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MDBSpinner } from 'mdb-react-ui-kit'
import AdminApi from './../../api/admin-api/admin-api'

import HeaderTopBar from './components/HeaderTopBar/HeaderTopBar.component'
import HeaderNavBar from './components/HeaderNavBar/HeaderNavBar.component'
import Breadcrumbs from './components/Breadcrumbs/Breadcrumbs.components'
import InsurancePlansHeroSection from './components/InsurancePlansHeroSection/InsurancePlansHeroSection.component'
import InsurancePlansSolutions from './components/InsurancePlansSolutions/InsurancePlansSolutions.component'
import FindAnAgent from './components/FindAnAgent/FindAnAgent.component'
import FooterContent from './components/FooterContent/FooterContent.component'
import FooterDisclaimer from './components/FooterDisclaimer/FooterDisclaimer.component'

import './InsurancePlans.page.scss'

const SLUG_MAP = {
  '/health-insurance': 'health_insurance_page',
  '/medicare-insurance': 'medicare_insurance_page',
  '/life-insurance': 'life_insurance_page',
  '/small-business-insurance': 'small_business_insurance_page',
}

const InsurancePlansPage = () => {
  const { pathname } = useLocation()
  const [config, setConfig] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const slug = SLUG_MAP[pathname]

    if (!slug) {
      console.warn(`InsurancePlansPage: no slug mapping for route "${pathname}"`)
      setConfig(null)
      setError('Page not found.')
      setIsLoading(false)
      return
    }

    const loadConfig = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await AdminApi.getWebsiteContentBySlug(slug)
        const row = response?.data?.data
        if (!cancelled && row?.content_data) {
          const data = typeof row.content_data === 'string' ? JSON.parse(row.content_data) : row.content_data
          setConfig(data)
        } else if (!cancelled) {
          console.warn(`InsurancePlansPage: no content found for slug "${slug}"`)
          setConfig(null)
          setError('This page is currently unavailable. Please try again later.')
        }
      } catch (err) {
        console.error('InsurancePlansPage: failed to fetch config', err)
        if (!cancelled) {
          setConfig(null)
          setError('Unable to load page content. Please try again later.')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadConfig()
    return () => {
      cancelled = true
    }
  }, [pathname])

  useEffect(() => {
    if (config) {
      document.title = config.pageTitle
    }
  }, [config])

  if (isLoading) {
    return (
      <div id='InsurancePlansPage'>
        <HeaderTopBar />
        <HeaderNavBar />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <MDBSpinner />
        </div>
        <FooterContent />
        <FooterDisclaimer />
      </div>
    )
  }

  if (!config) {
    return (
      <div id='InsurancePlansPage'>
        <HeaderTopBar />
        <HeaderNavBar />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <h2 style={{ marginBottom: '1rem', color: '#333' }}>Oops! Something went wrong.</h2>
          <p style={{ fontSize: '1.1rem', color: '#666', maxWidth: '500px' }}>
            {error || 'This page is currently unavailable.'}
          </p>
          <a href='/' style={{ marginTop: '1.5rem', fontSize: '1rem', color: '#007bff' }}>
            Return to Home
          </a>
        </div>
        <FooterContent />
        <FooterDisclaimer />
      </div>
    )
  }

  return (
    <div id='InsurancePlansPage'>
      <HeaderTopBar />
      <HeaderNavBar />
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Insurance' }, { label: config.breadcrumbLabel }]} />
      <InsurancePlansHeroSection
        headline={config.heroHeadline}
        subtitle={config.heroSubtitle}
        headlineMobile={config.heroHeadlineMobile}
      />
      <InsurancePlansSolutions
        heading={config.solutionsHeading}
        body={config.solutionsBody}
        contentType={config.solutionContentType}
        items={config.solutionItems}
        image={config.solutionImage}
      />
      <FindAnAgent />
      <FooterContent />
      <FooterDisclaimer />
    </div>
  )
}

export default InsurancePlansPage
